import React from "react";

/**
 * Minimal, dependency-free Markdown renderer for the rule-agent explanation.
 *
 * Supports the subset the agent emits: ATX headings (#..######), bold (**x**),
 * inline code (`x`), unordered lists (- / *) with one level of nesting,
 * GFM pipe tables, horizontal rules (---), and paragraphs. Renders real React
 * nodes (no dangerouslySetInnerHTML), so it is safe against HTML injection.
 * React 16 compatible.
 */

const HR_RE = /^(-{3,}|\*{3,}|_{3,})$/;
const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const LIST_ITEM_RE = /^\s*[-*]\s+/;

function isBlockStart(trimmed) {
    return (
        trimmed === "" ||
        HR_RE.test(trimmed) ||
        HEADING_RE.test(trimmed) ||
        LIST_ITEM_RE.test(trimmed) ||
        trimmed.startsWith("|")
    );
}

function isTableSeparator(line) {
    let t = (line || "").trim();
    return t.includes("-") && /^\|?[\s:|-]+\|?$/.test(t);
}

function splitTableRow(line) {
    let t = line.trim();
    if (t.startsWith("|")) t = t.slice(1);
    if (t.endsWith("|")) t = t.slice(0, -1);
    return t.split("|").map((cell) => cell.trim());
}

// Tokenize inline text into plain strings, **bold** and `code`.
function renderInline(text, keyPrefix) {
    let nodes = [];
    let regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
    let lastIndex = 0;
    let match;
    let idx = 0;
    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            nodes.push(text.slice(lastIndex, match.index));
        }
        let token = match[0];
        if (token.startsWith("**")) {
            nodes.push(<strong key={keyPrefix + "-b" + idx}>{token.slice(2, -2)}</strong>);
        } else {
            nodes.push(
                <code
                    key={keyPrefix + "-c" + idx}
                    style={{
                        fontFamily: "monospace",
                        background: "#f0f2f5",
                        borderRadius: "3px",
                        padding: "1px 4px",
                        fontSize: "0.9em",
                    }}
                >
                    {token.slice(1, -1)}
                </code>
            );
        }
        lastIndex = regex.lastIndex;
        idx++;
    }
    if (lastIndex < text.length) {
        nodes.push(text.slice(lastIndex));
    }
    return nodes;
}

function MarkdownRenderer({ source }) {
    if (!source) return null;

    let lines = source.replace(/\r\n/g, "\n").split("\n");
    let blocks = [];
    let i = 0;
    let key = 0;

    while (i < lines.length) {
        let line = lines[i];
        let trimmed = line.trim();

        if (trimmed === "") {
            i++;
            continue;
        }

        if (HR_RE.test(trimmed)) {
            blocks.push(
                <hr key={key++} style={{ border: "none", borderTop: "1px solid #e0e0e0", margin: "12px 0" }} />
            );
            i++;
            continue;
        }

        let heading = trimmed.match(HEADING_RE);
        if (heading) {
            let level = heading[1].length;
            let fontSize = level <= 2 ? "1.05em" : "1em";
            blocks.push(
                <div
                    key={key++}
                    className="blueText"
                    style={{ fontWeight: "bold", fontSize, margin: "10px 0 4px" }}
                >
                    {renderInline(heading[2], "h" + key)}
                </div>
            );
            i++;
            continue;
        }

        // GFM pipe table: a "| ... |" row immediately followed by a separator row.
        if (trimmed.startsWith("|") && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
            let header = splitTableRow(trimmed);
            i += 2; // consume header + separator
            let rows = [];
            while (i < lines.length && lines[i].trim().startsWith("|")) {
                rows.push(splitTableRow(lines[i]));
                i++;
            }
            blocks.push(
                <div key={key++} style={{ overflowX: "auto", margin: "8px 0" }}>
                    <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.9em" }}>
                        <thead>
                            <tr>
                                {header.map((cell, ci) => (
                                    <th
                                        key={ci}
                                        style={{
                                            border: "1px solid #e0e0e0",
                                            padding: "6px 8px",
                                            textAlign: "left",
                                            background: "#f0f2f5",
                                        }}
                                    >
                                        {renderInline(cell, "th" + key + "-" + ci)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, ri) => (
                                <tr key={ri}>
                                    {header.map((_, ci) => (
                                        <td
                                            key={ci}
                                            style={{ border: "1px solid #e0e0e0", padding: "6px 8px", verticalAlign: "top" }}
                                        >
                                            {renderInline(row[ci] || "", "td" + key + "-" + ri + "-" + ci)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
            continue;
        }

        // Unordered list (with one level of nesting via leading whitespace).
        if (LIST_ITEM_RE.test(line)) {
            let items = [];
            while (i < lines.length && LIST_ITEM_RE.test(lines[i])) {
                let raw = lines[i];
                let indent = raw.length - raw.replace(/^\s+/, "").length;
                let content = raw.trim().replace(/^[-*]\s+/, "");
                items.push({ content, nested: indent >= 2 });
                i++;
            }
            blocks.push(
                <ul key={key++} style={{ margin: "4px 0", paddingLeft: "20px" }}>
                    {items.map((item, ii) => (
                        <li key={ii} style={{ marginLeft: item.nested ? "18px" : 0, marginBottom: "2px" }}>
                            {renderInline(item.content, "li" + key + "-" + ii)}
                        </li>
                    ))}
                </ul>
            );
            continue;
        }

        // Paragraph: accumulate consecutive non-block lines.
        let paraLines = [trimmed];
        i++;
        while (i < lines.length && !isBlockStart(lines[i].trim())) {
            paraLines.push(lines[i].trim());
            i++;
        }
        blocks.push(
            <p key={key++} style={{ margin: "4px 0" }}>
                {renderInline(paraLines.join(" "), "p" + key)}
            </p>
        );
    }

    return <div>{blocks}</div>;
}

export default MarkdownRenderer;
